/**
 * PublicPageLayout
 *
 * Marketing-styled shell for the public (no-auth) pages — Marketing, Support,
 * Accessibility, and the legal policies. Provides a branded top bar, a gradient
 * hero with decorative artwork, a content area, and a rich footer with links to
 * every public page. Two body styles:
 *   - variant="marketing" (default): full-width sectioned content on a light
 *     background — for landing-style pages.
 *   - variant="document": content wrapped in a readable bordered card — for the
 *     long-form legal policies.
 *
 * Public and standalone; not part of the authenticated app chrome.
 */

import { ReactNode, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Box,
  Container,
  Stack,
  Group,
  Title,
  Text,
  Anchor,
  Button,
  Badge,
  Card,
  Image,
  SimpleGrid,
  Burger,
  Menu,
  rem,
  useComputedColorScheme,
} from '@mantine/core';

export type PublicVariant = 'marketing' | 'document';

/** Primary top-bar navigation (the legal policies live in the footer). */
const NAV: { to: string; label: string }[] = [
  { to: '/marketing', label: 'Overview' },
  { to: '/support', label: 'Support' },
  { to: '/accessibility', label: 'Accessibility' },
];

const FOOTER_LINKS: { heading: string; links: { to: string; label: string }[] }[] = [
  {
    heading: 'Product',
    links: [
      { to: '/marketing', label: 'Overview' },
      { to: '/support', label: 'Support' },
      { to: '/accessibility', label: 'Accessibility' },
    ],
  },
  {
    heading: 'Legal',
    links: [
      { to: '/privacy', label: 'Privacy Policy' },
      { to: '/terms', label: 'Terms of Service' },
      { to: '/cookies', label: 'Cookies Policy' },
      { to: '/data-deletion', label: 'Delete my data' },
    ],
  },
];

const HERO_GRADIENT = 'linear-gradient(135deg, #4c6ef5 0%, #6741d9 55%, #7048e8 100%)';

export interface PublicPageLayoutProps {
  /** Small label above the title (e.g. "CCMS Apps · Support"). */
  eyebrow?: string;
  title: string;
  subtitle?: ReactNode;
  /** Icon chip shown in the hero. */
  icon?: ReactNode;
  /** Hero call-to-action buttons / content. */
  actions?: ReactNode;
  /** Optional artwork rendered on the right of the hero (marketing landing). */
  art?: ReactNode;
  variant?: PublicVariant;
  children: ReactNode;
}

export default function PublicPageLayout({
  eyebrow,
  title,
  subtitle,
  icon,
  actions,
  art,
  variant = 'marketing',
  children,
}: PublicPageLayoutProps) {
  // Follow Mantine's app-level color scheme (NOT the OS via CSS light-dark(),
  // which ignores the in-app toggle).
  const scheme = useComputedColorScheme('light');
  const dark = scheme === 'dark';
  const { pathname } = useLocation();
  const [menuOpened, setMenuOpened] = useState(false);

  return (
    <Box
      style={{
        minHeight: '100vh',
        background: dark ? 'var(--mantine-color-dark-7)' : '#f6f7fb',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Top bar */}
      <Container size="lg" w="100%" py="md">
        <Group justify="space-between" wrap="nowrap">
          <Anchor component={Link} to="/marketing" underline="never">
            <Group gap={8}>
              <Image src="/logo.png" alt="CCMS" h={28} w="auto" fit="contain" />
              <Text fw={800} style={{ letterSpacing: '-0.3px' }}>CCMS</Text>
            </Group>
          </Anchor>

          {/* Desktop nav */}
          <Group gap={rem(28)} visibleFrom="sm">
            {NAV.map((n) => {
              const active = pathname === n.to;
              return (
                <Anchor
                  key={n.to}
                  component={Link}
                  to={n.to}
                  size="sm"
                  fw={active ? 700 : 500}
                  c={active ? undefined : 'dimmed'}
                  underline="never"
                >
                  {n.label}
                </Anchor>
              );
            })}
          </Group>

          <Group gap="sm" wrap="nowrap">
            <Button component={Link} to="/auth/login" variant="light" radius="xl" size="sm" visibleFrom="sm">
              Sign in
            </Button>

            {/* Mobile menu */}
            <Menu position="bottom-end" shadow="md" width={200} opened={menuOpened} onChange={setMenuOpened}>
              <Menu.Target>
                <Burger opened={menuOpened} hiddenFrom="sm" size="sm" aria-label="Open navigation" />
              </Menu.Target>
              <Menu.Dropdown>
                {NAV.map((n) => (
                  <Menu.Item key={n.to} component={Link} to={n.to} fw={pathname === n.to ? 700 : undefined}>
                    {n.label}
                  </Menu.Item>
                ))}
                <Menu.Divider />
                <Menu.Item component={Link} to="/auth/login">Sign in</Menu.Item>
              </Menu.Dropdown>
            </Menu>
          </Group>
        </Group>
      </Container>

      {/* Hero */}
      <Box
        style={{
          position: 'relative',
          overflow: 'hidden',
          background: HERO_GRADIENT,
          color: '#fff',
          borderRadius: rem(28),
          margin: `0 ${rem(16)} ${rem(8)}`,
        }}
      >
        <HeroDecoration />
        <Container size="lg" py={rem(art ? 56 : 64)} style={{ position: 'relative', zIndex: 1 }}>
          <SimpleGrid cols={{ base: 1, md: art ? 2 : 1 }} spacing={rem(40)} verticalSpacing={rem(28)}>
            <Stack gap="md" align={art ? 'flex-start' : 'center'} maw={art ? undefined : 760} mx={art ? undefined : 'auto'}>
              {icon && (
                <Box
                  style={{
                    width: rem(56), height: rem(56), borderRadius: rem(16),
                    background: 'rgba(255,255,255,0.16)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: '#fff',
                  }}
                >
                  {icon}
                </Box>
              )}
              {eyebrow && (
                <Badge
                  variant="white"
                  radius="sm"
                  styles={{ root: { background: 'rgba(255,255,255,0.16)', color: '#fff' } }}
                >
                  {eyebrow}
                </Badge>
              )}
              <Title
                order={1}
                ta={art ? 'left' : 'center'}
                style={{ fontSize: rem(art ? 42 : 38), lineHeight: 1.1, letterSpacing: '-0.5px', fontWeight: 800 }}
              >
                {title}
              </Title>
              {subtitle && (
                <Text ta={art ? 'left' : 'center'} size="lg" style={{ color: 'rgba(255,255,255,0.9)', maxWidth: rem(620) }}>
                  {subtitle}
                </Text>
              )}
              {actions && <Group mt="sm" justify={art ? 'flex-start' : 'center'}>{actions}</Group>}
            </Stack>

            {art && (
              <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {art}
              </Box>
            )}
          </SimpleGrid>
        </Container>
      </Box>

      {/* Content */}
      <Container size={variant === 'document' ? 'md' : 'lg'} py={rem(48)} style={{ flex: 1, width: '100%' }}>
        {variant === 'document' ? (
          <Card withBorder radius="lg" p={{ base: 'lg', sm: 'xl' }} shadow="sm">
            <Stack gap="lg">{children}</Stack>
          </Card>
        ) : (
          <Stack gap={rem(40)}>{children}</Stack>
        )}
      </Container>

      {/* Footer */}
      <Box style={{ background: '#0e1330', color: 'rgba(255,255,255,0.7)' }}>
        <Container size="lg" py={rem(40)}>
          <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="xl">
            <Stack gap="xs">
              <Group gap={8}>
                <Image src="/logo_512.png" alt="CCMS" h={30} w={30} fit="contain" radius="sm" />
                <Text fw={800} c="white">CCMS</Text>
              </Group>
              <Text size="sm" style={{ maxWidth: rem(280) }}>
                A family of apps for Acts Christian Church teams — tasks, people, bookings,
                procurement and learning, on iPhone, Android and the web.
              </Text>
            </Stack>
            {FOOTER_LINKS.map((col) => (
              <Stack key={col.heading} gap={6}>
                <Text fw={700} c="white" size="sm">{col.heading}</Text>
                {col.links.map((l) => (
                  <Anchor key={l.to} component={Link} to={l.to} size="sm" c="gray.4">
                    {l.label}
                  </Anchor>
                ))}
                {col.heading === 'Product' && (
                  <Anchor href="mailto:developer@actschurch.co.za" size="sm" c="gray.4">
                    Contact us
                  </Anchor>
                )}
              </Stack>
            ))}
          </SimpleGrid>
          <Text size="xs" mt="xl" c="gray.6">
            © 2026 Acts Christian Church. All rights reserved.
          </Text>
        </Container>
      </Box>
    </Box>
  );
}

/** Subtle dotted + glow decoration behind the hero content. */
function HeroDecoration() {
  return (
    <svg
      aria-hidden="true"
      style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: 0.5, pointerEvents: 'none' }}
    >
      <defs>
        <pattern id="pp-dots" width="26" height="26" patternUnits="userSpaceOnUse">
          <circle cx="2" cy="2" r="2" fill="rgba(255,255,255,0.14)" />
        </pattern>
        <radialGradient id="pp-glow" cx="18%" cy="8%" r="60%">
          <stop offset="0" stopColor="rgba(255,255,255,0.35)" />
          <stop offset="1" stopColor="rgba(255,255,255,0)" />
        </radialGradient>
      </defs>
      <rect width="100%" height="100%" fill="url(#pp-dots)" />
      <rect width="100%" height="100%" fill="url(#pp-glow)" />
    </svg>
  );
}
